import type { StudyMode } from "@/lib/supabase/types";

// Google Ads conversion labels
const GOOGLE_ADS_REGISTER_LABEL = "AW-17665884814/wyjYCNKDnYEcEI6F4OdB";
const GOOGLE_ADS_ACTIVATION_LABEL =
  process.env.NEXT_PUBLIC_GOOGLE_ADS_ACTIVATION_LABEL || "";
const GOOGLE_ADS_PAID_LABEL =
  process.env.NEXT_PUBLIC_GOOGLE_ADS_PAID_LABEL || "";

declare global {
  interface Window {
    posthog?: {
      __loaded?: boolean;
      capture: (event: string, props?: Record<string, unknown>) => void;
      identify: (
        distinctId: string,
        properties?: Record<string, unknown>
      ) => void;
      register: (properties: Record<string, unknown>) => void;
      people: {
        set: (properties: Record<string, unknown>) => void;
      };
      reset: () => void;
      conversations?: {
        show: () => void;
        hide: () => void;
      };
    };
    gtag?: (...args: unknown[]) => void;
    fbq?: (...args: unknown[]) => void;
  }
}

/** Check if PostHog is fully loaded (not just the stub) */
function isPostHogReady(): boolean {
  return !!window.posthog?.__loaded;
}

// Queue for events fired before PostHog loads
type QueuedEntry =
  | { type: "capture"; name: string; props?: Record<string, unknown> }
  | { type: "identify"; id: string; props?: Record<string, unknown> }
  | { type: "register"; props: Record<string, unknown> }
  | { type: "people_set"; props: Record<string, unknown> };

let eventQueue: QueuedEntry[] = [];
let flushing = false;

function flushQueue() {
  if (flushing || !isPostHogReady()) return;
  flushing = true;
  const pending = eventQueue;
  eventQueue = [];
  for (const entry of pending) {
    if (entry.type === "capture") {
      window.posthog!.capture(entry.name, entry.props);
    } else if (entry.type === "identify") {
      window.posthog!.identify(entry.id, entry.props);
    } else if (entry.type === "register") {
      window.posthog!.register(entry.props);
    } else {
      window.posthog!.people.set(entry.props);
    }
  }
  flushing = false;
}

function waitForPostHog() {
  let attempts = 0;
  const interval = setInterval(() => {
    attempts++;
    if (isPostHogReady()) {
      clearInterval(interval);
      flushQueue();
    } else if (attempts >= 50) {
      clearInterval(interval); // give up after ~5s
    }
  }, 100);
}

// Queue for gtag conversions fired before the script loads
let gtagQueue: Array<{ sendTo: string; value?: number; currency: string }> = [];

function flushGtagQueue() {
  if (!window.gtag || gtagQueue.length === 0) return;
  const pending = gtagQueue;
  gtagQueue = [];
  for (const entry of pending) {
    window.gtag("event", "conversion", {
      send_to: entry.sendTo,
      ...(entry.value != null && { value: entry.value, currency: entry.currency }),
    });
  }
}

function waitForGtag() {
  let attempts = 0;
  const interval = setInterval(() => {
    attempts++;
    if (window.gtag) {
      clearInterval(interval);
      flushGtagQueue();
    } else if (attempts >= 50) {
      clearInterval(interval); // give up after ~5s
    }
  }, 100);
}

/** Identify user in PostHog */
export function identifyUser(
  userId: string,
  properties?: Record<string, unknown>
) {
  if (isPostHogReady()) {
    window.posthog!.identify(userId, properties);
  } else {
    eventQueue.push({ type: "identify", id: userId, props: properties });
    waitForPostHog();
  }
}

/** Reset PostHog identity (on logout) */
export function resetIdentity() {
  if (isPostHogReady()) window.posthog!.reset();
}

/** Register super-properties — attached to every subsequent event in the session */
export function registerSuperProperties(props: Record<string, unknown>) {
  if (isPostHogReady()) {
    window.posthog!.register(props);
  } else {
    eventQueue.push({ type: "register", props });
    waitForPostHog();
  }
}

/** Set person properties — persistent on the user profile */
export function setPersonProperties(props: Record<string, unknown>) {
  if (isPostHogReady()) {
    window.posthog!.people.set(props);
  } else {
    eventQueue.push({ type: "people_set", props });
    waitForPostHog();
  }
}

/** PostHog event */
export function trackEvent(name: string, props?: Record<string, unknown>) {
  if (isPostHogReady()) {
    window.posthog!.capture(name, props);
  } else {
    eventQueue.push({ type: "capture", name, props });
    waitForPostHog();
  }
}

/** Google Ads conversion */
export function trackConversion(
  sendTo: string,
  value?: number,
  currency = "USD"
) {
  if (window.gtag) {
    window.gtag("event", "conversion", {
      send_to: sendTo,
      ...(value != null && { value, currency }),
    });
  } else {
    gtagQueue.push({ sendTo, value, currency });
    waitForGtag();
  }
}

/** Meta Pixel event */
export function trackFBEvent(
  event: string,
  params?: Record<string, unknown>
) {
  window.fbq?.("track", event, params);
}

/** Track CTA click (PostHog only) */
export function trackCTA(location: string) {
  trackEvent("cta_clicked", { location });
}

/** Track buy/pricing click (PostHog + Meta) */
export function trackBuyClick(plan: string, price: number) {
  trackEvent("buy_clicked", { plan, price });
  trackFBEvent("InitiateCheckout", {
    content_name: plan,
    value: price,
    currency: "USD",
  });
}

/** Track user registration / magic link sent (all services, fires once per browser) */
export function trackRegister(email: string) {
  const key = "snapcards_register_tracked";
  if (typeof window !== "undefined" && localStorage.getItem(key)) return;

  trackEvent("user_registered", { email });
  trackConversion(GOOGLE_ADS_REGISTER_LABEL);
  trackFBEvent("Lead");

  localStorage.setItem(key, "1");
}

/** Track FAQ open (PostHog only) */
export function trackFAQOpen(question: string) {
  trackEvent("faq_opened", { question });
}

// ── P0: Core Funnel ──

/** Track successful deck generation from PDF */
export function trackDeckGenerated(
  deckId: string,
  cardCount: number,
  fileName: string
) {
  const ext = fileName.split(".").pop()?.toLowerCase() || "unknown";
  trackEvent("deck_generated", {
    deck_id: deckId,
    card_count: cardCount,
    file_name: fileName,
    file_type: ext,
  });
}

/** Track failed generation (parse or AI) */
export function trackGenerationFailed(
  error: string,
  fileName: string,
  stage: "parse" | "generate" | "upload"
) {
  const ext = fileName.split(".").pop()?.toLowerCase() || "unknown";
  trackEvent("generation_failed", { error, file_name: fileName, stage, file_type: ext });
}

/** Track study session start */
export function trackStudySessionStarted(
  deckId: string,
  sessionId: string,
  cardCount: number,
  mode?: StudyMode
) {
  trackEvent("study_session_started", {
    deck_id: deckId,
    session_id: sessionId,
    card_count: cardCount,
    ...(mode && { study_mode: mode }),
  });
}

/** Track study session completion */
export function trackStudySessionCompleted(props: {
  deckId: string;
  sessionId: string;
  cardCount: number;
  correctCount: number;
  accuracy: number;
  durationSeconds: number;
  mode?: StudyMode;
}) {
  trackEvent("study_session_completed", {
    deck_id: props.deckId,
    session_id: props.sessionId,
    card_count: props.cardCount,
    correct_count: props.correctCount,
    accuracy: props.accuracy,
    duration_seconds: props.durationSeconds,
    ...(props.mode && { study_mode: props.mode }),
  });
}

/** Track successful deck export */
export function trackDeckExported(
  deckId: string,
  format: "anki" | "csv",
  cardCount: number
) {
  trackEvent("deck_exported", {
    deck_id: deckId,
    format,
    card_count: cardCount,
  });
}

/** Track failed export */
export function trackExportFailed(
  deckId: string,
  format: "anki" | "csv",
  error: string
) {
  trackEvent("export_failed", { deck_id: deckId, format, error });
}

// ── P1: Retention & Engagement ──

/** Track free tier limit hit */
export function trackLimitHit(
  limitType: "pdfs_per_month" | "max_decks" | "anki_export",
  tier: string
) {
  trackEvent("limit_hit", { limit_type: limitType, tier });
}

/** Track deck cloned from library */
export function trackDeckCloned(
  sourceDeckId: string,
  newDeckId: string,
  cardCount: number
) {
  trackEvent("deck_cloned", {
    source_deck_id: sourceDeckId,
    new_deck_id: newDeckId,
    card_count: cardCount,
  });
}

/** Track deck rated in library */
export function trackDeckRated(deckId: string, rating: number) {
  trackEvent("deck_rated", { deck_id: deckId, rating });
}

/** Track card edited/added/deleted */
export function trackCardEdited(
  deckId: string,
  action: "edit" | "add" | "delete"
) {
  trackEvent("card_edited", { deck_id: deckId, action });
}

/** Track deck deleted */
export function trackDeckDeleted(deckId: string, cardCount: number) {
  trackEvent("deck_deleted", { deck_id: deckId, card_count: cardCount });
}

// ── P2: Nice-to-have ──

/** Track auth method used */
export function trackAuthMethod(
  method: "google" | "email",
  mode: "signin" | "signup"
) {
  trackEvent("auth_method_used", { method, mode });
}

/** Track milestone shown */
export function trackMilestoneShown(milestoneType: string) {
  trackEvent("milestone_shown", { milestone_type: milestoneType });
}

/** Card-cap hit (free tier ceiling reached during generation) */
export function trackCardCapHit(props: {
  deck_id: string;
  card_count: number;
  source: string;
}) {
  trackEvent("card_cap_hit", props);
}

/** Upgrade funnel events */
export function trackUpgradePromptShown(location: string) {
  trackEvent("upgrade_prompt_shown", { location });
}

export function trackUpgradePromptDismissed(location: string) {
  trackEvent("upgrade_prompt_dismissed", { location });
}

export function trackUpgradeModalOpened(props: {
  location: string;
  price_shown: number;
  annual_price_shown: number;
  country: string;
  ppp_tier: string;
}) {
  trackEvent("upgrade_modal_opened", props);
}

export function trackPlanSelected(props: {
  plan: "monthly" | "annual";
  price: number;
  country: string;
  ppp_tier: string;
}) {
  trackEvent("plan_selected", props);
}

export function trackSubscribeClicked(props: {
  plan: "monthly" | "annual";
  price: number;
  country: string;
  ppp_tier: string;
}) {
  trackEvent("subscribe_clicked", props);
}

export function trackEarlyAdopterClaimed(props: {
  plan: "monthly" | "annual";
  price_shown: number;
  country: string;
  ppp_tier: string;
}) {
  trackEvent("early_adopter_claimed", props);
}

export function trackSubscriptionCompleted() {
  trackEvent("subscription_completed");
  if (GOOGLE_ADS_PAID_LABEL) {
    trackConversion(GOOGLE_ADS_PAID_LABEL);
  }
  trackFBEvent("Purchase", { currency: "USD" });
}

/** Track library search */
export function trackLibrarySearched(
  query: string,
  category: string,
  resultCount: number
) {
  trackEvent("library_searched", {
    query,
    category,
    result_count: resultCount,
  });
}

/** Track qualified activation — first study session completed (Google Ads secondary conversion) */
export function trackQualifiedActivation() {
  trackEvent("qualified_activation");
  if (GOOGLE_ADS_ACTIVATION_LABEL) {
    trackConversion(GOOGLE_ADS_ACTIVATION_LABEL);
  }
}

// ── Chat (G3) ──

export function trackChatOpened(props: { card_id: string; deck_id: string; tier: string }) {
  trackEvent("chat_opened", props);
}

export function trackChatMessageSent(props: {
  card_id: string;
  tier: string;
  message_chars: number;
  history_depth: number;
}) {
  trackEvent("chat_message_sent", props);
}

export function trackChatFirstTokenMs(props: { card_id: string; tier: string; ms: number }) {
  trackEvent("chat_first_token_ms", props);
}

export function trackChatMessageCompleted(props: {
  card_id: string;
  tier: string;
  response_chars: number;
  total_ms: number;
}) {
  trackEvent("chat_message_completed", props);
}

export function trackChatRateLimitHit(props: { tier: string; messages_used: number }) {
  trackEvent("chat_rate_limit_hit", props);
}

// ── PWA ──

type InstallSurface = "dashboard_card";
type InstallPlatform = "android" | "ios" | "desktop";
type NotifSurface = "dashboard_card" | "post_install" | "settings";
type NotifOutcome = "granted" | "denied" | "dismissed";

export function trackInstallPromptShown(platform: InstallPlatform, surface: InstallSurface = "dashboard_card") {
  trackEvent("pwa_install_prompt_shown", { platform, surface });
}

export function trackInstallPromptAccepted(platform: InstallPlatform) {
  trackEvent("pwa_install_prompt_accepted", { platform });
}

export function trackInstallPromptDismissed(platform: InstallPlatform) {
  trackEvent("pwa_install_prompt_dismissed", { platform });
}

/** Fires from window 'appinstalled' — the actual install event */
export function trackPWAInstalled() {
  trackEvent("pwa_installed");
}

export function trackNotificationPromptShown(surface: NotifSurface) {
  trackEvent("pwa_notification_prompt_shown", { surface });
}

export function trackNotificationPermissionResult(outcome: NotifOutcome, surface: NotifSurface) {
  trackEvent("pwa_notification_permission_result", { outcome, surface });
}

export function trackNotificationPromptDismissed(surface: NotifSurface) {
  trackEvent("pwa_notification_prompt_dismissed", { surface });
}

export function trackPushUnsubscribed(surface: NotifSurface = "settings") {
  trackEvent("pwa_push_unsubscribed", { surface });
}

export function trackOfflineSessionStarted(deckId: string, cardCount: number) {
  trackEvent("pwa_offline_session_started", { deck_id: deckId, card_count: cardCount });
}

export function trackOfflineSyncCompleted(sessionCount: number, reviewCount: number) {
  trackEvent("pwa_offline_sync_completed", { session_count: sessionCount, review_count: reviewCount });
}
